import * as React from 'react';
import * as AvatarPrimitive from '@radix-ui/react-avatar';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@noor/lib/cn';

/**
 * Avatar root. Deliberately NOT `overflow-hidden`: an `AvatarStatus` presence
 * dot sits on the bottom-right edge and its contrast ring would be sheared off
 * by a rounded clip on the root. The image and fallback clip themselves
 * (`rounded-full overflow-hidden`) instead, so the portrait is still a circle.
 */
function Avatar({
  className,
  ...props
}: React.ComponentProps<typeof AvatarPrimitive.Root>) {
  return (
    <AvatarPrimitive.Root
      data-slot="avatar"
      className={cn('relative flex size-10 shrink-0 rounded-full', className)}
      {...props}
    />
  );
}

function AvatarImage({
  className,
  ...props
}: React.ComponentProps<typeof AvatarPrimitive.Image>) {
  return (
    <AvatarPrimitive.Image
      data-slot="avatar-image"
      className={cn('aspect-square size-full overflow-hidden rounded-full', className)}
      {...props}
    />
  );
}

function AvatarFallback({
  className,
  ...props
}: React.ComponentProps<typeof AvatarPrimitive.Fallback>) {
  return (
    <AvatarPrimitive.Fallback
      data-slot="avatar-fallback"
      className={cn(
        'flex size-full items-center justify-center overflow-hidden rounded-full bg-avatar-bg text-sm font-medium text-avatar-fg',
        className,
      )}
      {...props}
    />
  );
}

/**
 * Presence dot overlaid on the bottom-right of an `Avatar`. Opt-in: drop it in
 * as the last child of `<Avatar>` (which is `relative`). The `ring` punches a
 * hole in the avatar edge so the dot reads as floating on the card surface.
 */
const avatarStatusVariants = cva(
  'absolute right-0 bottom-0 block rounded-full ring-2 ring-card',
  {
    variants: {
      status: {
        online: 'bg-status-success',
        offline: 'bg-status-neutral',
        busy: 'bg-status-danger',
        away: 'bg-status-pending',
      },
      size: {
        sm: 'size-2',
        default: 'size-2.5',
        lg: 'size-3.5',
      },
    },
    defaultVariants: {
      status: 'online',
      size: 'default',
    },
  },
);

function AvatarStatus({
  className,
  status,
  size,
  ...props
}: React.ComponentProps<'span'> & VariantProps<typeof avatarStatusVariants>) {
  return (
    <span
      data-slot="avatar-status"
      data-status={status ?? 'online'}
      className={cn(avatarStatusVariants({ status, size }), className)}
      {...props}
    />
  );
}

export { Avatar, AvatarImage, AvatarFallback, AvatarStatus, avatarStatusVariants };
